import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { 
  FileText,
  Download,
  Calendar,
  TrendingUp,
  Users,
  CreditCard,
  Building,
  BarChart3,
  PieChart,
  Filter,
  Search,
  RefreshCw,
  Eye,
  Mail
} from 'lucide-react'

const Reports = () => {
  const [selectedDateRange, setSelectedDateRange] = useState('last30days')
  const [selectedReport, setSelectedReport] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)

  // Mock report data
  const reportTemplates = [
    {
      id: 'revenue-summary',
      title: 'Revenue Summary Report',
      description: 'Comprehensive overview of fee collection and revenue trends',
      icon: TrendingUp,
      category: 'Financial',
      estimatedTime: '2-3 minutes',
      formats: ['PDF', 'Excel', 'CSV'],
      color: 'text-green-600'
    },
    {
      id: 'student-enrollment',
      title: 'Student Enrollment Report',
      description: 'Detailed analysis of student admissions and enrollment patterns',
      icon: Users,
      category: 'Academic',
      estimatedTime: '1-2 minutes',
      formats: ['PDF', 'Excel'],
      color: 'text-blue-600'
    },
    {
      id: 'payment-transactions',
      title: 'Payment Transactions Report',
      description: 'Complete transaction history with payment methods and status',
      icon: CreditCard,
      category: 'Financial',
      estimatedTime: '3-5 minutes',
      formats: ['PDF', 'Excel', 'CSV'],
      color: 'text-purple-600'
    },
    {
      id: 'college-performance',
      title: 'College Performance Report',
      description: 'Comparative analysis of college-wise performance metrics',
      icon: Building,
      category: 'Performance',
      estimatedTime: '2-4 minutes',
      formats: ['PDF', 'Excel'],
      color: 'text-orange-600'
    },
    {
      id: 'fee-collection',
      title: 'Fee Collection Analytics',
      description: 'Detailed breakdown of fee collection rates and trends',
      icon: BarChart3,
      category: 'Financial',
      estimatedTime: '2-3 minutes',
      formats: ['PDF', 'Excel', 'CSV'],
      color: 'text-indigo-600'
    },
    {
      id: 'outstanding-dues',
      title: 'Outstanding Dues Report',
      description: 'List of pending payments and overdue accounts',
      icon: PieChart,
      category: 'Financial',
      estimatedTime: '1-2 minutes',
      formats: ['PDF', 'Excel'],
      color: 'text-red-600'
    }
  ]

  const recentReports = [
    {
      id: '1',
      name: 'Monthly Revenue Report - November 2024',
      type: 'Revenue Summary',
      generatedAt: '2024-12-01T10:30:00',
      size: '2.4 MB',
      format: 'PDF',
      downloadUrl: '#'
    },
    {
      id: '2',
      name: 'Student Enrollment - Fall 2024',
      type: 'Student Enrollment',
      generatedAt: '2024-11-28T14:15:00',
      size: '1.8 MB',
      format: 'Excel',
      downloadUrl: '#'
    },
    {
      id: '3',
      name: 'Payment Transactions - Q3 2024',
      type: 'Payment Transactions',
      generatedAt: '2024-11-25T09:45:00',
      size: '5.2 MB',
      format: 'CSV',
      downloadUrl: '#'
    }
  ]

  const handleGenerateReport = async (reportId: string) => {
    setSelectedReport(reportId)
    setIsGenerating(true)
    // Simulate report generation
    await new Promise(resolve => setTimeout(resolve, 3000))
    setIsGenerating(false)
    setSelectedReport(null)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Financial': return 'bg-green-100 text-green-800'
      case 'Academic': return 'bg-blue-100 text-blue-800'
      case 'Performance': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600">Generate comprehensive reports and insights</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <select 
              value={selectedDateRange}
              onChange={(e) => setSelectedDateRange(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="last7days">Last 7 days</option>
              <option value="last30days">Last 30 days</option>
              <option value="last3months">Last 3 months</option>
              <option value="last6months">Last 6 months</option>
              <option value="lastyear">Last year</option>
              <option value="custom">Custom range</option>
            </select>
          </div>
          
          <Button variant="outline" className="flex items-center space-x-2">
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Reports</p>
                  <p className="text-2xl font-bold text-gray-900">127</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">This Month</p>
                  <p className="text-2xl font-bold text-gray-900">23</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Downloads</p>
                  <p className="text-2xl font-bold text-gray-900">1,847</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Download className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Scheduled</p>
                  <p className="text-2xl font-bold text-gray-900">5</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Calendar className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report Templates */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Available Reports</CardTitle>
              <CardDescription>Select a report template to generate insights</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {reportTemplates.map((report, index) => {
                  const Icon = report.icon
                  return (
                    <motion.div
                      key={report.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.1 }}
                      className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="p-2 bg-gray-50 rounded-lg">
                          <Icon className={`h-5 w-5 ${report.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 mb-1">{report.title}</h4>
                          <p className="text-sm text-gray-600 mb-3">{report.description}</p>
                          
                          <div className="flex items-center justify-between mb-3">
                            <Badge variant="outline" className={getCategoryColor(report.category)}>
                              {report.category}
                            </Badge>
                            <span className="text-xs text-gray-500">{report.estimatedTime}</span>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex space-x-1">
                              {report.formats.map((format) => (
                                <span 
                                  key={format} 
                                  className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded"
                                >
                                  {format}
                                </span>
                              ))}
                            </div>
                            
                            <Button
                              size="sm"
                              onClick={() => handleGenerateReport(report.id)}
                              disabled={isGenerating && selectedReport === report.id}
                              className="flex items-center space-x-1"
                            >
                              {isGenerating && selectedReport === report.id ? (
                                <>
                                  <RefreshCw className="h-3 w-3 animate-spin" />
                                  <span>Generating...</span>
                                </>
                              ) : (
                                <>
                                  <Download className="h-3 w-3" />
                                  <span>Generate</span>
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Reports */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Recent Reports</CardTitle>
              <CardDescription>Recently generated and downloaded reports</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentReports.map((report, index) => (
                  <motion.div
                    key={report.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h5 className="font-medium text-gray-900 truncate">{report.name}</h5>
                        <p className="text-sm text-gray-600">{report.type}</p>
                        <div className="flex items-center space-x-4 mt-2">
                          <span className="text-xs text-gray-500">
                            {formatDate(report.generatedAt)}
                          </span>
                          <span className="text-xs text-gray-500">{report.size}</span>
                          <Badge variant="outline" className="text-xs">
                            {report.format}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-1 ml-2">
                        <Button variant="ghost" size="sm">
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Download className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Button variant="outline" className="w-full flex items-center space-x-2">
                  <FileText className="h-4 w-4" />
                  <span>View All Reports</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Scheduled Reports */}
          <Card>
            <CardHeader>
              <CardTitle>Scheduled Reports</CardTitle>
              <CardDescription>Automated report generation schedule</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div>
                    <p className="font-medium text-blue-900">Monthly Revenue</p>
                    <p className="text-sm text-blue-700">Every 1st of month</p>
                  </div>
                  <Badge className="bg-blue-100 text-blue-800">Active</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div>
                    <p className="font-medium text-green-900">Weekly Collections</p>
                    <p className="text-sm text-green-700">Every Monday</p>
                  </div>
                  <Badge className="bg-green-100 text-green-800">Active</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div>
                    <p className="font-medium text-orange-900">Outstanding Dues</p>
                    <p className="text-sm text-orange-700">Every Friday</p>
                  </div>
                  <Badge className="bg-orange-100 text-orange-800">Active</Badge>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Button variant="outline" className="w-full flex items-center space-x-2">
                  <Calendar className="h-4 w-4" />
                  <span>Manage Schedule</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default Reports
